// QRDownloadStep.jsx - Complete file using Context
import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import {
  Download,
  Share2,
  Copy,
  Check,
  ArrowLeft,
  AlertCircle,
  Loader,
} from 'lucide-react';
import { useQRContext } from '../context/QRContext';
import { useQRDownload } from './QRDownloadUtils';
import { trackEvent } from '../config/firebase';

const StepContent = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  padding: 3rem;
  min-height: 600px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const StepTitle = styled.h2`
  color: white;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const StepDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.125rem;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const DownloadSection = styled.div`
  flex: 1;
  margin-bottom: 2rem;
`;

const DownloadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const DownloadCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(124, 58, 237, 0.2);
    transform: translateY(-2px);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const CardIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(45deg, #7c3aed, #ec4899);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const CardTitle = styled.h3`
  color: white;
  font-size: 1.125rem;
  font-weight: 600;
`;

const CardDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const OptionGroup = styled.div`
  margin-bottom: 1rem;
`;

const OptionLabel = styled.div`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const OptionButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${({ active }) =>
    active ? 'rgba(124, 58, 237, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid
    ${({ active }) =>
      active ? 'rgba(124, 58, 237, 0.5)' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  color: ${({ active }) => (active ? '#ffffff' : 'rgba(255, 255, 255, 0.8)')};
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(124, 58, 237, 0.2);
    border-color: rgba(124, 58, 237, 0.3);
  }
`;

const ActionButton = styled.button`
  width: 100%;
  padding: 0.75rem 1.5rem;
  background: ${({ variant }) =>
    variant === 'secondary'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'linear-gradient(45deg, #7c3aed, #ec4899)'};
  color: white;
  border: ${({ variant }) =>
    variant === 'secondary' ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'};
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  position: relative;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ variant }) =>
      variant === 'secondary'
        ? '0 4px 15px rgba(255, 255, 255, 0.1)'
        : '0 8px 25px rgba(124, 58, 237, 0.4)'};
    background: ${({ variant }) =>
      variant === 'secondary'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'linear-gradient(45deg, #7c3aed, #ec4899)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin: 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  background: ${({ type }) => {
    switch (type) {
      case 'success':
        return 'rgba(34, 197, 94, 0.1)';
      case 'error':
        return 'rgba(239, 68, 68, 0.1)';
      case 'loading':
        return 'rgba(59, 130, 246, 0.1)';
      default:
        return 'rgba(156, 163, 175, 0.1)';
    }
  }};
  border: 1px solid
    ${({ type }) => {
      switch (type) {
        case 'success':
          return 'rgba(34, 197, 94, 0.2)';
        case 'error':
          return 'rgba(239, 68, 68, 0.2)';
        case 'loading':
          return 'rgba(59, 130, 246, 0.2)';
        default:
          return 'rgba(156, 163, 175, 0.2)';
      }
    }};
  color: ${({ type }) => {
    switch (type) {
      case 'success':
        return '#22c55e';
      case 'error':
        return '#ef4444';
      case 'loading':
        return '#3b82f6';
      default:
        return '#9ca3af';
    }
  }};
`;

const ContentPreview = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  word-break: break-all;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.8);
  max-height: 100px;
  overflow-y: auto;
  font-family: monospace;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
  }
`;

const ShareActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1rem;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 1rem;
  margin-top: auto;
`;

const SecondaryButton = styled.button`
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const TipsSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const TipsTitle = styled.h4`
  color: white;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TipsGrid = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  line-height: 1.5;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const TipItem = styled.div`
  strong {
    color: white;
  }
`;

const QRDownloadStep = () => {
  const { formData, qrOptions, contentType, previousStep, addToHistory, getContentData } = useQRContext();
  const savedRef = useRef(false);

  const [selectedFormat, setSelectedFormat] = useState('PNG');
  const [selectedSize, setSelectedSize] = useState('1024');
  const [downloadStatus, setDownloadStatus] = useState(null);
  const [shareStatus, setShareStatus] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const { downloadQR, copyQRToClipboard, getShareableURL } = useQRDownload();

  // Save to history once when user reaches the download step
  useEffect(() => {
    if (savedRef.current) return;
    const contentData = getContentData();
    if (!contentData) return;
    savedRef.current = true;
    addToHistory({
      id: Date.now().toString(),
      type: contentType,
      data: contentData,
      options: { ...qrOptions },
      createdAt: new Date().toISOString(),
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formats = [
    { id: 'PNG', label: 'PNG', description: 'High quality with transparency' },
    { id: 'JPG', label: 'JPG', description: 'Smaller file size' },
    { id: 'SVG', label: 'SVG', description: 'Vector format, scalable' },
    { id: 'PDF', label: 'PDF', description: 'Print ready format' },
  ];

  const sizes = [
    { id: '512', label: '512×512', description: 'Standard' },
    { id: '1024', label: '1024×1024', description: 'High quality' },
    { id: '2048', label: '2048×2048', description: 'Ultra high' },
    { id: '4096', label: '4096×4096', description: 'Maximum' },
  ];

  /**
   * Downloads the QR code in the selected format and resolution.
   * Logs download_initiated event to Firebase Analytics.
   */
  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadStatus(null);

    try {
      const result = await downloadQR(
        formData,
        contentType,
        qrOptions,
        selectedFormat,
        selectedSize,
      );

      if (result.success) {
        // Track download event
        trackEvent('download_initiated', {
          format: selectedFormat.toLowerCase(),
          resolution: selectedSize,
        });

        setDownloadStatus({
          type: 'success',
          message: `Successfully downloaded ${result.filename}.${selectedFormat.toLowerCase()}`,
        });
      } else {
        setDownloadStatus({
          type: 'error',
          message: result.error || 'Download failed',
        });
      }
    } catch (error) {
      setDownloadStatus({
        type: 'error',
        message: `Download failed: ${error.message}`,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Copies the QR code image to clipboard.
   */
  const handleCopyToClipboard = async () => {
    setIsSharing(true);
    setShareStatus(null);

    try {
      const result = await copyQRToClipboard(
        formData,
        contentType,
        qrOptions,
        512,
      );

      if (result.success) {
        setShareStatus({
          type: 'success',
          message: 'QR code copied to clipboard!',
        });
      } else {
        setShareStatus({
          type: 'error',
          message: result.error || 'Failed to copy to clipboard',
        });
      }
    } catch (error) {
      setShareStatus({
        type: 'error',
        message: `Copy failed: ${error.message}`,
      });
    } finally {
      setIsSharing(false);
    }
  };

  /**
   * Shares the QR code using native Web Share API if available.
   */
  const handleShare = async () => {
    setIsSharing(true);
    setShareStatus(null);

    try {
      if (navigator.share) {
        // Use native share if available
        const result = await getShareableURL(
          formData,
          contentType,
          qrOptions,
          512,
        );

        if (result.success) {
          // Convert data URL to blob for sharing
          const response = await fetch(result.dataURL);
          const blob = await response.blob();
          const file = new File([blob], 'qrcode.png', { type: 'image/png' });

          await navigator.share({
            title: 'QR Code',
            text: 'Check out this QR code',
            files: [file],
          });

          setShareStatus({
            type: 'success',
            message: 'QR code shared successfully!',
          });
        } else {
          throw new Error(result.error);
        }
      } else {
        // Fallback to copy to clipboard
        await handleCopyToClipboard();
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // User cancelled the share
        setShareStatus(null);
      } else {
        setShareStatus({
          type: 'error',
          message: `Share failed: ${error.message}`,
        });
      }
    } finally {
      setIsSharing(false);
    }
  };

  /**
   * Clears the status message and action status.
   */
  const clearStatus = (statusType) => {
    if (statusType === 'download') {
      setDownloadStatus(null);
    } else {
      setShareStatus(null);
    }
  };

  const getContentDisplayData = () => {
    switch (contentType) {
      case 'url':
        return formData.url;
      case 'text':
        return formData.text.length > 100
          ? `${formData.text.substring(0, 100)}...`
          : formData.text;
      case 'contact': {
        const { firstName, lastName, email, phone } = formData.contact;
        return (
          `${firstName} ${lastName}`.trim() || email || phone || 'Contact Card'
        );
      }
      default:
        return '';
    }
  };

  // Auto-clear status messages after 5 seconds
  useEffect(() => {
    if (downloadStatus) {
      const timer = setTimeout(() => clearStatus('download'), 5000);
      return () => clearTimeout(timer);
    }
  }, [downloadStatus]);

  useEffect(() => {
    if (shareStatus) {
      const timer = setTimeout(() => clearStatus('share'), 5000);
      return () => clearTimeout(timer);
    }
  }, [shareStatus]);

  return (
    <StepContent>
      <StepTitle>Your QR Code is Ready!</StepTitle>
      <StepDescription>
        Download your QR code in various formats or share it directly with
        others.
      </StepDescription>

      <DownloadSection>
        <DownloadGrid>
          <DownloadCard>
            <CardHeader>
              <CardIcon>
                <Download size={20} />
              </CardIcon>
              <CardTitle>Download QR Code</CardTitle>
            </CardHeader>
            <CardDescription>
              Get your QR code in high quality formats suitable for print and
              digital use.
            </CardDescription>

            <OptionGroup>
              <OptionLabel>Format:</OptionLabel>
              <ButtonGroup>
                {formats.map((format) => (
                  <OptionButton
                    key={format.id}
                    active={selectedFormat === format.id}
                    onClick={() => setSelectedFormat(format.id)}
                    title={format.description}
                  >
                    {format.label}
                  </OptionButton>
                ))}
              </ButtonGroup>
            </OptionGroup>

            <OptionGroup>
              <OptionLabel>Size:</OptionLabel>
              <ButtonGroup>
                {sizes.map((size) => (
                  <OptionButton
                    key={size.id}
                    active={selectedSize === size.id}
                    onClick={() => setSelectedSize(size.id)}
                    title={size.description}
                  >
                    {size.label}
                  </OptionButton>
                ))}
              </ButtonGroup>
            </OptionGroup>

            {downloadStatus && (
              <StatusMessage type={downloadStatus.type}>
                {downloadStatus.type === 'success' && <Check size={16} />}
                {downloadStatus.type === 'error' && <AlertCircle size={16} />}
                {downloadStatus.message}
              </StatusMessage>
            )}

            <ActionButton onClick={handleDownload} disabled={isDownloading}>
              {isDownloading ? (
                <>
                  <Loader size={16} className="animate-spin" />
                  Preparing Download...
                </>
              ) : (
                <>
                  <Download size={16} />
                  Download {selectedFormat}
                </>
              )}
            </ActionButton>
          </DownloadCard>

          <DownloadCard>
            <CardHeader>
              <CardIcon>
                <Share2 size={20} />
              </CardIcon>
              <CardTitle>Share & Copy</CardTitle>
            </CardHeader>
            <CardDescription>
              Quickly share your QR code or copy it to clipboard for easy
              distribution.
            </CardDescription>

            <ContentPreview>{getContentDisplayData()}</ContentPreview>

            {shareStatus && (
              <StatusMessage type={shareStatus.type}>
                {shareStatus.type === 'success' && <Check size={16} />}
                {shareStatus.type === 'error' && <AlertCircle size={16} />}
                {shareStatus.type === 'loading' && (
                  <Loader size={16} className="animate-spin" />
                )}
                {shareStatus.message}
              </StatusMessage>
            )}

            <ShareActions>
              <ActionButton
                variant="secondary"
                onClick={handleCopyToClipboard}
                disabled={isSharing}
              >
                {isSharing ? (
                  <Loader size={16} className="animate-spin" />
                ) : shareStatus?.type === 'success' &&
                  shareStatus.message.includes('clipboard') ? (
                  <Check size={16} />
                ) : (
                  <Copy size={16} />
                )}
                {shareStatus?.type === 'success' &&
                shareStatus.message.includes('clipboard')
                  ? 'Copied!'
                  : 'Copy to Clipboard'}
              </ActionButton>

              <ActionButton
                variant="secondary"
                onClick={handleShare}
                disabled={isSharing}
              >
                {isSharing ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Share2 size={16} />
                )}
                Share QR Code
              </ActionButton>
            </ShareActions>
          </DownloadCard>
        </DownloadGrid>

        <TipsSection>
          <TipsTitle>💡 Download Tips</TipsTitle>
          <TipsGrid>
            <TipItem>
              <strong>PNG:</strong> Best for digital use with transparency
              support. Ideal for websites and apps.
            </TipItem>
            <TipItem>
              <strong>JPG:</strong> Smaller file sizes, good for email and
              social media sharing.
            </TipItem>
            <TipItem>
              <strong>SVG:</strong> Vector format that scales perfectly. Great
              for professional printing.
            </TipItem>
            <TipItem>
              <strong>PDF:</strong> Print-ready format. Perfect for business
              cards and marketing materials.
            </TipItem>
          </TipsGrid>
        </TipsSection>
      </DownloadSection>

      <NavigationButtons>
        <SecondaryButton onClick={previousStep}>
          <ArrowLeft size={16} />
          Back to Customize
        </SecondaryButton>
      </NavigationButtons>
    </StepContent>
  );
};

export default QRDownloadStep;
